import { getUiLocale } from '../../utils/uiTranslation';
import { ref, get, set, update } from 'firebase/database';
import { database } from '../../firebase';
import { atsAnalyticsApi, emitAtsAnalyticsEvent, invalidateAnalyticsSessionCache } from '../../services/atsAnalyticsService';
import {
  buildActorAnalyticsContext,
  buildCandidateAnalyticsFields,
  normalizeAnalyticsSource
} from '../../utils/analyticsEntityFields';
import { limitFactorLabel } from './candidateEvaluationJourney';

/**
 * Helper function to recursively remove undefined values from an object
 * Firebase RTDB throws errors when undefined values are in the update object
 */
const sanitizeForFirebase = (obj) => {
  if (obj === null || obj === undefined) return null;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(sanitizeForFirebase);

  const sanitized = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      sanitized[key] = sanitizeForFirebase(value);
    }
  }
  return sanitized;
};

const normalizeDateInputValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const TALENT_POOL_EXTRACT_SOURCE = 'frontend_ats_transition';
const JOB_OPTIONS_CACHE_TTL_MS = 30 * 1000;
const jobOptionsCache = new Map();

const cloneJobOption = (option) => ({
  ...option,
  data: option?.data && typeof option.data === 'object'
    ? { ...option.data }
    : option?.data
});

const buildCustomListJobRegistryRecord = (listId, listData = {}, timestamp = Date.now()) => {
  const targetHireDate = normalizeDateInputValue(listData?.hiringPlan?.targetHireDate || listData?.targetHireDate || '');
  const isoTimestamp = new Date(timestamp).toISOString();
  const snapshot = {
    ...listData,
    id: listId,
    roleId: listId,
    jobId: listId,
    jobTitle: listData.title || listData.name || listId,
    jobOpening: listData.title || listData.name || listId,
    targetHireDate,
    hiringPlan: {
      ...(listData.hiringPlan || {}),
      targetHireDate
    },
    type: 'custom_list',
    isCustomList: true
  };

  return sanitizeForFirebase({
    jobId: listId,
    jobTitle: snapshot.jobTitle,
    department: listData.department || '',
    location: listData.location || '',
    targetHireDate,
    hiringPlan: snapshot.hiringPlan,
    createdAt: listData.createdAt || timestamp,
    updatedAt: isoTimestamp,
    lifecycleStatus: listData.isActive === false ? 'archived' : 'active',
    published: false,
    deleted: false,
    lastKnownSnapshot: snapshot,
    schemaVersion: 1
  });
};

/**
 * Queue backend AI parsing for a talent-pool profile.
 * This is best-effort and should never block ATS stage transitions.
 */
const triggerTalentPoolExtraction = async (
  companyUid,
  applicantId,
  source = TALENT_POOL_EXTRACT_SOURCE,
  options = {}
) => {
  if (!companyUid || !applicantId) return;
  try {
    await fetch(`${atsAnalyticsApi.backendUrl}/analytics/v2/talent-pool/extract`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        company_uid: companyUid,
        applicant_id: applicantId,
        source,
        priority: options.priority || null,
        force: Boolean(options.force),
        requested_by_user: options.requestedByUser || null
      })
    });
  } catch (error) {
    console.warn('Talent-pool extraction trigger failed:', error);
  }
};

/**
 * Bulk enqueue parsing work so high-volume imports do not fan out into thousands
 * of single API calls from the browser.
 */
const triggerTalentPoolExtractionBulk = async (
  companyUid,
  applicantIds,
  source = 'bulk_upload',
  options = {}
) => {
  if (!companyUid || !Array.isArray(applicantIds) || !applicantIds.length) return;
  try {
    await fetch(`${atsAnalyticsApi.backendUrl}/analytics/v2/talent-pool/extract/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        company_uid: companyUid,
        applicant_ids: applicantIds,
        source,
        priority: options.priority || null,
        force: Boolean(options.force),
        requested_by_user: options.requestedByUser || null
      })
    });
  } catch (error) {
    console.warn('Talent-pool bulk extraction trigger failed:', error);
  }
};

/**
 * Ensure first-time rejected ATS candidates are represented in talent pool storage.
 * Imported candidates already have profile IDs; authenticated candidates get mirrored
 * into imported_profiles/{companyUid}/{applicantId} for long-term rediscovery.
 */
const ensureRejectedCandidateInTalentPool = async (connectionKey, connectionData) => {
  const employerUid = connectionData?.employerUid || connectionData?.employerId;
  const applicantId = connectionData?.jobSeekerUid || connectionData?.tempProfileId;
  if (!employerUid || !applicantId) return;

  const targetRef = ref(database, `imported_profiles/${employerUid}/${applicantId}`);
  const targetSnap = await get(targetRef);

  // Keep existing imported profiles; just refresh timestamps/extraction.
  if (targetSnap.exists()) {
    await update(targetRef, {
      lastUpdated: Date.now(),
      source: targetSnap.val()?.source || (connectionData?.applicantType === 'imported' ? 'cv_upload' : 'ats_rejected_first_time')
    });
    await triggerTalentPoolExtraction(employerUid, applicantId, 'reject_existing_profile', { priority: 'high' });
    return;
  }

  let personData = {};
  try {
    const personSnap = await get(ref(database, `people/${applicantId}`));
    if (personSnap.exists()) {
      personData = personSnap.val() || {};
    }
  } catch (error) {
    console.warn('Could not read people profile during reject-to-pool sync:', error);
  }

  // Normalize CV object from different historical shapes.
  let cvPayload = null;
  const personCv = personData?.cv;
  const resumeLink = personData?.resumeLink || personData?.resumeURL || '';
  if (typeof personCv === 'string' && personCv) {
    cvPayload = { Link: personCv, fileName: 'resume.pdf', uploadedAt: Date.now() };
  } else if (personCv && typeof personCv === 'object') {
    const link = personCv.Link || personCv.link || personCv.url || '';
    if (link) {
      cvPayload = {
        Link: link,
        fileName: personCv.fileName || personCv.filename || 'resume.pdf',
        uploadedAt: personCv.uploadedAt || Date.now()
      };
    }
  }
  if (!cvPayload && resumeLink) {
    cvPayload = { Link: resumeLink, fileName: 'resume.pdf', uploadedAt: Date.now() };
  }

  const timestamp = Date.now();
  const profilePayload = sanitizeForFirebase({
    name: personData?.name || connectionData?.applicantName || 'Unknown',
    email: personData?.email || connectionData?.applicantEmail || '',
    phone: personData?.phone || '',
    linkedin: personData?.linkedin || '',
    cv: cvPayload,
    parsedData: personData?.parsedData || {},
    uploadedBy: employerUid,
    source: connectionData?.applicantType === 'imported' ? 'cv_upload' : 'ats_rejected_first_time',
    originalUid: connectionData?.applicantType === 'authenticated' ? applicantId : undefined,
    createdAt: timestamp,
    lastUpdated: timestamp,
    isArchived: true,
    assigned_jobs: [],
    rejectedFromConnection: connectionKey
  });

  await set(targetRef, profilePayload);
  await triggerTalentPoolExtraction(employerUid, applicantId, 'reject_new_profile', { priority: 'high' });
};

/**
 * DatabaseOptimizer - Optimized Firebase operations using indexed structure
 * 
 * New Database Structure:
 * - companies/{EMPLOYER_UID}/applicant_index/by_job/{JOB_ID}/{CONNECTION_KEY}
 * - people/{JOB_SEEKER_UID}/application_index/applications/{CONNECTION_KEY}
 * - connections/{CONNECTION_KEY}/ (enhanced with jobId, applicantType)
 * - imported_profiles/{EMPLOYER_UID}/{PROFILE_ID}/ (for uploaded CVs and archived applicants)
 */

// ============================================================================
// CORE UTILITY FUNCTIONS
// ============================================================================

/**
 * Generate connection key based on applicant type
 */
export const generateConnectionKey = (employerId, applicantId, applicantType = 'authenticated') => {
  if (applicantType === 'authenticated') {
    return `${employerId}_${applicantId}`;
  } else {
    return `${employerId}_imported_${applicantId}`;
  }
};

/**
 * Parse connection key to extract employer and applicant info
 */
export const parseConnectionKey = (connectionKey) => {
  if (connectionKey.includes('_imported_')) {
    const parts = connectionKey.split('_imported_');
    // Strip any copy suffix (e.g., _copy_123456789) from imported applicant IDs
    const rawApplicantId = parts[1] || '';
    const baseApplicantId = rawApplicantId.split('_copy_')[0];
    return {
      employerUid: parts[0],
      applicantId: baseApplicantId,
      applicantType: 'imported'
    };
  } else {
    const parts = connectionKey.split('_');
    return {
      employerUid: parts[0],
      applicantId: parts[1],
      applicantType: 'authenticated'
    };
  }
};

const normalizeFitScoreValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  if (!Number.isInteger(numeric)) return null;
  return Math.max(0, Math.min(100, numeric));
};

const normalizeEvaluationStrings = (value, limit = 6) => {
  const values = Array.isArray(value) ? value : (value ? [value] : []);
  return values
    .map(item => String(item || '').trim())
    .filter(Boolean)
    .slice(0, limit);
};

/**
 * Build the immutable initial /100 score event for a newly-created connection.
 * CV imports run filtering before a connection key exists, so this object is
 * attached later inside the same root multipath update that creates the
 * connection and employer index.
 */
export const buildInitialCandidateEvaluation = (
  evaluation,
  fitScore,
  passedFilter,
  fallbackTimestamp = Date.now()
) => {
  const normalizedScore = normalizeFitScoreValue(fitScore ?? evaluation?.fit_score);
  const status = evaluation?.status || (normalizedScore !== null ? 'scored' : 'technical_failure');
  if (status !== 'scored' || normalizedScore === null) return null;

  const suppliedLedger = evaluation?.candidateEvaluation;
  const suppliedScore = normalizeFitScoreValue(suppliedLedger?.current?.fitScore);
  if (
    suppliedScore === normalizedScore
    && suppliedLedger?.initialEventId
    && suppliedLedger?.events?.[suppliedLedger.initialEventId]
  ) {
    // Filtering happens before an imported connection exists. Reuse the
    // backend's validated ledger verbatim in the later atomic creation write.
    return suppliedLedger;
  }

  const strengths = normalizeEvaluationStrings(evaluation?.strengths, 6);
  const improvementAreas = normalizeEvaluationStrings(
    evaluation?.improvementAreas || evaluation?.improvement_areas || evaluation?.concerns,
    6
  );
  const rawFactors = Array.isArray(evaluation?.factors) ? evaluation.factors : [];
  const factors = rawFactors
    .map((factor) => {
      const detail = typeof factor === 'object'
        ? String(factor?.detail || factor?.reason || '').trim()
        : String(factor || '').trim();
      const labelSource = typeof factor === 'object'
        ? factor?.label || factor?.title || detail
        : detail;
      const label = limitFactorLabel(labelSource);
      return label ? { label, detail: detail || label } : null;
    })
    .filter(Boolean)
    .slice(0, 3);

  if (!factors.length) {
    const fallbackDetail = strengths[0]
      || improvementAreas[0]
      || 'Evidence compared with the role requirements.';
    factors.push({
      label: limitFactorLabel(fallbackDetail) || 'Role Evidence Reviewed',
      detail: fallbackDetail
    });
  }

  const parsedTimestamp = Number(evaluation?.evaluatedAt);
  const evaluatedAt = Number.isFinite(parsedTimestamp) && parsedTimestamp > 0
    ? parsedTimestamp
    : fallbackTimestamp;
  const eventId = 'initial_screening'; // Firebase-safe: no . # $ / [ ] characters.
  // v1_SHA256("initial_screening\0initial_screening"), matching the shared
  // journey module's safe source idempotency key.
  const sourceKey = 'v1_e47b68d00630a6685a5a199f68ca1ebf805f0aa84fdf7f16254228a2cbaf9b8c';
  const summary = String(evaluation?.summary || '').trim();
  const rationale = String(evaluation?.rationale || summary).trim();
  const decision = String(
    evaluation?.analysis || evaluation?.decision || (passedFilter ? 'proceed' : 'reject')
  ).toLowerCase() === 'reject' ? 'reject' : 'proceed';

  const scoreRevision = {
    before: null,
    after: normalizedScore,
    delta: null,
    // Aliases keep pre-connection imports readable by the shared cumulative
    // journey module while preserving the initial-ingestion wire contract.
    scoreBefore: null,
    scoreAfter: normalizedScore,
    scoreDelta: null,
    direction: 'initial',
    rationale,
    factors
  };
  const event = {
    eventId,
    revision: 1,
    sourceType: 'initial_screening',
    sourceId: 'initial_screening',
    status: 'scored',
    performanceScore: normalizedScore,
    performanceScoreScale: 100,
    scoreRevision,
    summary,
    strengths,
    improvementAreas,
    rationale,
    factors,
    decision,
    stageId: 'stage-1',
    substageId: 'initial-analysis',
    evaluatedAt,
    schemaVersion: 1
  };

  return {
    schemaVersion: 1,
    initialEventId: eventId,
    current: {
      fitScore: normalizedScore,
      eventId,
      revision: 1,
      sourceType: 'initial_screening',
      stageId: 'stage-1',
      substageId: 'initial-analysis',
      evaluatedAt
    },
    events: { [eventId]: event },
    sourceIndex: { [sourceKey]: eventId }
  };
};

// ============================================================================
// OPTIMIZED EMPLOYER OPERATIONS
// ============================================================================

/**
 * Load all applicants for an employer using optimized index
 * Replaces the expensive loadAllConnectionsAndUpdateDropdown
 */
export const loadEmployerApplicantsOptimized = async (employerUID) => {
  try {
    // Load employer's applicant index
    const indexRef = ref(database, `companies/${employerUID}/applicant_index/by_job`);
    const indexSnapshot = await get(indexRef);

    if (!indexSnapshot.exists()) {
      // Return a special signal that migration is needed
      return { sortedData: {}, allApplicants: [], needsMigration: true };
    }

    const jobsData = indexSnapshot.val();
    const sortedData = {};
    const allApplicants = [];

    // Process each job's applicants
    for (const [jobId, applicants] of Object.entries(jobsData)) {
      // Get job title from the first applicant's cached data
      const firstApplicant = Object.values(applicants)[0];
      const jobTitle = firstApplicant?.jobTitle || jobId;

      // Store connection keys for this job
      const connectionKeys = Object.keys(applicants);
      sortedData[jobTitle] = connectionKeys;

      // Build global applicant list for search
      Object.entries(applicants).forEach(([connectionKey, applicantData]) => {
        allApplicants.push({
          id: applicantData.applicantId,
          connectionKey,
          name: applicantData.applicantName || 'Unknown Name',
          jobTitle: applicantData.jobTitle || jobTitle,
          applicantType: applicantData.applicantType || 'authenticated',
          starred: applicantData.starred || false
        });
      });
    }

    return { sortedData, allApplicants };
  } catch (error) {
    console.error('Error loading employer applicants optimized:', error);
    return { sortedData: {}, allApplicants: [] };
  }
};

/**
 * Load applicants for a specific job using optimized index
 * Replaces the expensive displayApplicantsForJob
 */
export const loadJobApplicantsOptimized = async (employerUID, jobId, connectionKeys) => {
  try {
    if (!connectionKeys || connectionKeys.length === 0) {
      return [];
    }

    // Get cached applicant data from index
    const indexRef = ref(database, `companies/${employerUID}/applicant_index/by_job/${jobId}`);
    const indexSnapshot = await get(indexRef);

    if (!indexSnapshot.exists()) {
      return [];
    }

    const indexData = indexSnapshot.val();
    const applicants = [];

    // Process each connection key
    for (const connectionKey of connectionKeys) {
      if (indexData[connectionKey]) {
        const cachedData = indexData[connectionKey];

        applicants.push({
          id: cachedData.applicantId,
          connectionKey,
          name: cachedData.applicantName || 'Unknown Name',
          email: cachedData.applicantEmail || '',
          applicantType: cachedData.applicantType || 'authenticated',
          stageId: cachedData.stageId || 'stage-1',
          substageId: cachedData.substageId || 'all-applicants-1',
          lastUpdated: cachedData.lastUpdated,
          createdAt: cachedData.createdAt,
          jobTitle: cachedData.jobTitle,
          starred: cachedData.starred || false,
          firstOpenedAt: cachedData.firstOpenedAt || null,
          fit_score: normalizeFitScoreValue(cachedData.fit_score)
        });
      }
    }

    return applicants;
  } catch (error) {
    console.error('Error loading job applicants optimized:', error);
    return [];
  }
};

// ============================================================================
// OPTIMIZED WRITE OPERATIONS
// ============================================================================

/**
 * Create a new connection with proper indexing
 */
export const createConnectionOptimized = async (employerUID, applicantId, jobData, applicantProfile, applicantType = 'authenticated') => {
  try {
    const connectionKey = generateConnectionKey(employerUID, applicantId, applicantType);
    const timestamp = Date.now();
    const createdAtIso = new Date(timestamp).toISOString();
    const normalizedSource = normalizeAnalyticsSource({
      utmSource: applicantProfile?.utm_source || applicantProfile?.utmSource || applicantProfile?.sourceUtm || '',
      source: applicantProfile?.primarySource || applicantProfile?.source || applicantProfile?.uploadSource || '',
      sourceDetails: applicantProfile?.sourceDetails || applicantProfile?.origin || '',
      sourceCategory: applicantProfile?.sourceCategory || '',
      sourceTags: applicantProfile?.sourceTags || applicantProfile?.tags || [],
      applicantType
    });
    const candidateFields = buildCandidateAnalyticsFields({
      candidateId: applicantId,
      roleId: jobData?.id || '',
      jobTitle: jobData?.title || jobData?.jobOpening || '',
      department: jobData?.department || '',
      location: jobData?.companyLocation || jobData?.location || '',
      ownerRecruiterId: jobData?.ownerRecruiterId || '',
      ownerRecruiterName: jobData?.ownerRecruiterName || '',
      ownerRecruiterEmail: jobData?.ownerRecruiterEmail || '',
      sourceInfo: normalizedSource,
      tags: jobData?.tags || []
    });

    // Prepare connection data
    const connectionData = {
      employerUid: employerUID,
      [applicantType === 'authenticated' ? 'jobSeekerUid' : 'tempProfileId']: applicantId,
      applicantType,
      jobId: jobData.id,
      roleId: jobData.id,
      candidateId: applicantId,
      jobAppliedFor: jobData.title || jobData.jobOpening,
      department: jobData?.department || '',
      location: jobData?.companyLocation || jobData?.location || '',
      stageId: 'stage-1',
      substageId: 'all-applicants-1',
      accepted: true,
      createdAt: timestamp,
      lastStageChange: createdAtIso,
      canMessage: applicantType === 'authenticated',
      source: applicantType === 'imported' ? 'cv_upload' : 'direct_application',
      sourceDetails: applicantType === 'imported' ? 'bulk_cv_upload' : 'job_portal',
      ...candidateFields
    };

    // Prepare index data
    const indexData = {
      applicantId,
      candidateId: applicantId,
      applicantType,
      applicantName: applicantProfile.name || applicantProfile.username || 'Unknown Name',
      applicantEmail: applicantProfile.email || '',
      jobTitle: jobData.title || jobData.jobOpening,
      roleId: jobData?.id || '',
      department: jobData?.department || '',
      location: jobData?.companyLocation || jobData?.location || '',
      ownerRecruiterId: jobData?.ownerRecruiterId || '',
      stageId: 'stage-1',
      substageId: 'all-applicants-1',
      lastStageChange: createdAtIso,
      lastUpdated: timestamp,
      createdAt: timestamp,
      source: applicantType === 'imported' ? 'cv_upload' : 'direct_application',
      sourceDetails: applicantType === 'imported' ? 'bulk_cv_upload' : 'job_portal',
      primarySource: normalizedSource.primarySource,
      sourceCategory: normalizedSource.sourceCategory,
      sourceTags: normalizedSource.sourceTags
    };

    const updates = {};

    // 1. Create connection
    updates[`connections/${connectionKey}`] = connectionData;

    // 2. Update employer index
    updates[`companies/${employerUID}/applicant_index/by_job/${jobData.id}/${connectionKey}`] = indexData;

    await update(ref(database), updates);

    const jobPayload = {
      jobId: jobData?.id || '',
      jobTitle: jobData?.title || jobData?.jobOpening || ''
    };
    const subjectPayload = {
      connectionKey,
      applicantId,
      applicantType
    };
    const sourceContext = {
      source: applicantType === 'imported' ? 'cv_upload' : 'direct_application',
      sourceDetails: applicantType === 'imported' ? 'bulk_cv_upload' : 'job_portal',
      ...buildActorAnalyticsContext({
        ownerRecruiterId: candidateFields.ownerRecruiterId,
        ownerRecruiterEmail: candidateFields.ownerRecruiterEmail,
        primarySource: normalizedSource.primarySource,
        sourceCategory: normalizedSource.sourceCategory,
        sourceTags: normalizedSource.sourceTags
      })
    };

    emitAtsAnalyticsEvent({
      company_uid: employerUID,
      event_type: 'application_created',
      occurred_at: createdAtIso,
      subject: subjectPayload,
      job: jobPayload,
      context: sourceContext
    });

    if (applicantType === 'imported') {
      emitAtsAnalyticsEvent({
        company_uid: employerUID,
        event_type: 'applicant_imported_cv',
        occurred_at: createdAtIso,
        subject: subjectPayload,
        job: jobPayload,
        context: sourceContext
      });
    }

    return connectionKey;
  } catch (error) {
    console.error('Error creating connection optimized:', error);
    throw error;
  }
};

/**
 * Update applicant position with proper index maintenance
 * Replaces updateApplicantPositionInFirebase
 */
export const updateApplicantPositionOptimized = async (connectionKey, newStageId, newSubstageId, additionalData = {}) => {
  try {
    // First, get the connection data to know which indexes to update
    const connectionRef = ref(database, `connections/${connectionKey}`);
    const connectionSnapshot = await get(connectionRef);

    if (!connectionSnapshot.exists()) {
      throw new Error(`Connection ${connectionKey} not found`);
    }

    const connectionData = connectionSnapshot.val();

    // Extract connection data (handle both old and new field names for backwards compatibility)
    const employerUid = connectionData.employerUid || connectionData.employerId;
    const { jobSeekerUid, tempProfileId, applicantType, jobId } = connectionData;
    const applicantId = jobSeekerUid || tempProfileId;

    if (!employerUid || !jobId) {
      throw new Error(`Missing required fields: employerUid=${employerUid}, jobId=${jobId}`);
    }

    const timestamp = Date.now();
    const updates = {};

    // 1. Update connection
    updates[`connections/${connectionKey}/stageId`] = newStageId;
    updates[`connections/${connectionKey}/substageId`] = newSubstageId;
    const stageChangeIso = new Date().toISOString();
    updates[`connections/${connectionKey}/lastStageChange`] = stageChangeIso;
    updates[`connections/${connectionKey}/lastUpdated`] = timestamp; // Track last activity time

    // Add any additional connection data
    Object.keys(additionalData).forEach(key => {
      updates[`connections/${connectionKey}/${key}`] = additionalData[key];
    });

    // If moving to hired, set a persistent 'isHired' flag
    if (newSubstageId === 'hired-applicants' && !connectionData.isHired) {
      updates[`connections/${connectionKey}/isHired`] = true;
      updates[`connections/${connectionKey}/hiredDate`] = stageChangeIso;
    }

    // 2. Update employer index
    updates[`companies/${employerUid}/applicant_index/by_job/${jobId}/${connectionKey}/stageId`] = newStageId;
    updates[`companies/${employerUid}/applicant_index/by_job/${jobId}/${connectionKey}/substageId`] = newSubstageId;
    updates[`companies/${employerUid}/applicant_index/by_job/${jobId}/${connectionKey}/lastStageChange`] = stageChangeIso;
    updates[`companies/${employerUid}/applicant_index/by_job/${jobId}/${connectionKey}/lastUpdated`] = timestamp;

    const indexMirrorFields = [
      'candidateId',
      'roleId',
      'jobTitle',
      'department',
      'location',
      'ownerRecruiterId',
      'ownerRecruiterName',
      'ownerRecruiterEmail',
      'primarySource',
      'sourceCategory',
      'sourceTags',
      'currentOfferId',
      'offerId',
      'offerStatus',
      'offerSentAt',
      'offerAcceptedAt',
      'acceptedOfferAt',
      'offerDeclinedAt',
      'offerExpiresAt',
      'hired',
      'hiredDate',
      'rejected',
      'rejectedDate',
      'rejectionReason',
      'withdrawalReason'
    ];
    indexMirrorFields.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(additionalData, key)) {
        updates[`companies/${employerUid}/applicant_index/by_job/${jobId}/${connectionKey}/${key}`] = additionalData[key];
      }
    });

    // Also update terminal states in the index for faster analytics queries
    if (newSubstageId === 'hired-applicants' && !connectionData.isHired) {
      updates[`companies/${employerUid}/applicant_index/by_job/${jobId}/${connectionKey}/isHired`] = true;
      updates[`companies/${employerUid}/applicant_index/by_job/${jobId}/${connectionKey}/hired`] = true;
      updates[`companies/${employerUid}/applicant_index/by_job/${jobId}/${connectionKey}/hiredDate`] = stageChangeIso;
    }

    if (newSubstageId === 'rejected-applicants') {
      updates[`companies/${employerUid}/applicant_index/by_job/${jobId}/${connectionKey}/isRejected`] = true;
      updates[`companies/${employerUid}/applicant_index/by_job/${jobId}/${connectionKey}/rejected`] = true;
      updates[`companies/${employerUid}/applicant_index/by_job/${jobId}/${connectionKey}/rejectedDate`] = additionalData.rejectedDate || stageChangeIso;
    }

    if (additionalData.actorUserId) {
      updates[`connections/${connectionKey}/lastActorUserId`] = additionalData.actorUserId;
    }
    if (additionalData.actorUserEmail) {
      updates[`connections/${connectionKey}/lastActorUserEmail`] = additionalData.actorUserEmail;
    }

    await update(ref(database), updates);

    const subjectPayload = {
      connectionKey,
      applicantId,
      candidateId: connectionData.candidateId || applicantId,
      applicantType: applicantType || (String(connectionKey).includes('_imported_') ? 'imported' : 'authenticated')
    };
    const jobPayload = {
      jobId: jobId || '',
      roleId: connectionData.roleId || jobId || '',
      jobTitle: connectionData.jobAppliedFor || '',
      department: connectionData.department || '',
      location: connectionData.location || ''
    };
    const analyticsContext = buildActorAnalyticsContext({
      actorUserId: additionalData.actorUserId || connectionData.lastActorUserId || '',
      actorUserEmail: additionalData.actorUserEmail || '',
      ownerRecruiterId: additionalData.ownerRecruiterId || connectionData.ownerRecruiterId || '',
      ownerRecruiterEmail: additionalData.ownerRecruiterEmail || connectionData.ownerRecruiterEmail || '',
      primarySource: additionalData.primarySource || connectionData.primarySource || '',
      sourceCategory: additionalData.sourceCategory || connectionData.sourceCategory || '',
      sourceTags: additionalData.sourceTags || connectionData.sourceTags || [],
      extra: {
        stageId: newStageId,
        substageId: newSubstageId
      }
    });

    if (connectionData.stageId !== newStageId || connectionData.substageId !== newSubstageId) {
      emitAtsAnalyticsEvent({
        company_uid: employerUid,
        event_type: 'applicant_stage_transitioned',
        occurred_at: stageChangeIso,
        subject: subjectPayload,
        job: jobPayload,
        context: {
          fromStageId: connectionData.stageId || null,
          fromSubstageId: connectionData.substageId || null,
          toStageId: newStageId,
          toSubstageId: newSubstageId,
          ...analyticsContext
        }
      });
    }

    if (newSubstageId === 'hired-applicants') {
      emitAtsAnalyticsEvent({
        company_uid: employerUid,
        event_type: 'candidate_hired',
        occurred_at: stageChangeIso,
        subject: subjectPayload,
        job: jobPayload,
        context: {
          ...analyticsContext
        }
      });
    }

    if (newSubstageId === 'rejected-applicants') {
      emitAtsAnalyticsEvent({
        company_uid: employerUid,
        event_type: 'candidate_rejected',
        occurred_at: stageChangeIso,
        subject: subjectPayload,
        job: jobPayload,
        context: {
          ...analyticsContext
        }
      });

      // First-time reject policy: mirror candidate into talent-pool storage and trigger extraction.
      if (connectionData.substageId !== 'rejected-applicants') {
        try {
          await ensureRejectedCandidateInTalentPool(connectionKey, connectionData);
        } catch (error) {
          console.warn('Failed to sync first-time rejected applicant into talent pool:', error);
        }
      }
    }

    return { success: true, connectionData };
  } catch (error) {
    console.error('Error in updateApplicantPositionOptimized:', {
      message: error?.message,
      code: error?.code,
      connectionKey,
      newStageId,
      newSubstageId
    });
    throw error;
  }
};

/**
 * Helper function to fetch email from Firebase Auth via cloud function
 */
const fetchUserEmail = async (uid) => {
  try {
    const response = await fetch('https://us-central1-jobseeqr.cloudfunctions.net/getUserEmail', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid })
    });
    const data = await response.json();
    return data.email || '';
  } catch (e) {
    console.warn('Could not fetch email via cloud function:', e);
    return '';
  }
};

/**
 * Archive connection data to imported profile history before deletion
 * Preserves chat, notes, scores, and assessment results
 */
export const archiveConnection = async (connectionKey) => {
  try {
    console.log('📦 Archiving connection:', connectionKey);

    // 1. Get connection data
    const connectionRef = ref(database, `connections/${connectionKey}`);
    const connectionSnapshot = await get(connectionRef);

    if (!connectionSnapshot.exists()) {
      console.warn('⚠️ Connection not found for archiving:', connectionKey);
      return;
    }

    const connectionData = connectionSnapshot.val();
    const employerUid = connectionData.employerUid || connectionData.employerId;
    const { jobSeekerUid, tempProfileId, applicantType, jobId, jobAppliedFor } = connectionData;
    const applicantId = jobSeekerUid || tempProfileId;

    if (!employerUid) {
      console.error('❌ No employer ID found in connection data');
      return;
    }

    console.log('📦 Archive - Connection details:', { employerUid, applicantId, applicantType, jobId });

    // 2. Get Applicant Profile Data (to ensure we have base profile)
    let profileData = {
      name: 'Unknown',
      email: '',
      phone: '',
      cv: {},
      linkedin: '',
      source: applicantType === 'authenticated' ? 'platform_applicant' : (applicantType || 'cv_upload'),
      originalUid: applicantType === 'authenticated' ? applicantId : null
    };

    // FIRST: Try to get data from the Employer Index (reliable source for name/email)
    try {
      const indexRef = ref(database, `companies/${employerUid}/applicant_index/by_job/${jobId}/${connectionKey}`);
      const indexSnap = await get(indexRef);
      if (indexSnap.exists()) {
        const indexData = indexSnap.val();
        console.log('📦 Found index data:', indexData);
        if (indexData.applicantName && indexData.applicantName !== 'Unknown Name') {
          profileData.name = indexData.applicantName;
          console.log('✅ Got name from employer index:', profileData.name);
        }
        if (indexData.applicantEmail) {
          profileData.email = indexData.applicantEmail;
        }
        // Use index source if available
        if (indexData.source) {
          profileData.source = indexData.source;
        }
      }
    } catch (e) {
      console.warn('⚠️ Could not read employer index:', e);
    }

    // SECOND: For authenticated users, ALWAYS try the people node for CV and profile data
    if (applicantType === 'authenticated') {
      try {
        const personRef = ref(database, `people/${applicantId}`);
        const personSnap = await get(personRef);
        const person = personSnap.val() || {};

        console.log('📦 Reading people node for authenticated user:', person);

        // Get name if still unknown
        if (!profileData.name || profileData.name === 'Unknown') {
          if (person.username) {
            profileData.name = person.username;
            console.log('✅ Got name from people node:', profileData.name);
          } else if (person.name) {
            profileData.name = person.name;
          }
        }

        // ALWAYS get CV from people node for authenticated users
        if (person.cv) {
          // Handle various CV storage formats
          let cvLink = '';
          if (typeof person.cv === 'string') {
            // CV stored as direct URL string
            cvLink = person.cv;
          } else if (person.cv.Link) {
            cvLink = person.cv.Link;
          } else if (person.cv.link) {
            cvLink = person.cv.link;
          } else if (person.cv.url) {
            cvLink = person.cv.url;
          }

          if (cvLink) {
            profileData.cv = {
              Link: cvLink,
              fileName: (typeof person.cv === 'object' ? (person.cv.fileName || person.cv.filename) : null) || 'resume.pdf',
              uploadedAt: (typeof person.cv === 'object' ? person.cv.uploadedAt : null) || Date.now()
            };
            console.log('✅ Got CV from people node:', profileData.cv.Link);
          }
        }

        profileData.phone = profileData.phone || person.phone || '';
        profileData.linkedin = profileData.linkedin || person.linkedin || '';
        profileData.originalUid = applicantId;

        // For authenticated users, if we still don't have email, fetch via cloud function
        if (!profileData.email) {
          console.log('📧 Fetching email via cloud function for:', applicantId);
          const fetchedEmail = await fetchUserEmail(applicantId);
          if (fetchedEmail) {
            profileData.email = fetchedEmail;
            console.log('✅ Got email from cloud function:', profileData.email);
          }
        }
      } catch (e) {
        console.warn('Could not fetch authenticated person profile:', e);
      }
    }

    // THIRD: For imported users, try the imported_profiles node
    if (applicantType === 'imported' || applicantType !== 'authenticated') {
      try {
        let profileSnap = await get(ref(database, `imported_profiles/${employerUid}/${applicantId}`));
        if (!profileSnap.exists()) {
          profileSnap = await get(ref(database, `imported_profiles/${applicantId}`));
        }

        if (profileSnap.exists()) {
          const existingProfile = profileSnap.val();
          console.log('📦 Found existing imported profile:', existingProfile);

          // Only overwrite if we have better data
          if (existingProfile.name && existingProfile.name !== 'Unknown' && (!profileData.name || profileData.name === 'Unknown')) {
            profileData.name = existingProfile.name;
          }
          profileData.email = profileData.email || existingProfile.email || '';
          profileData.phone = profileData.phone || existingProfile.phone || '';

          // Get CV from imported profile if we don't have one
          if ((!profileData.cv || !profileData.cv.Link) && existingProfile.cv) {
            profileData.cv = existingProfile.cv;
            console.log('✅ Got CV from imported profile:', profileData.cv.Link || profileData.cv);
          }

          profileData.linkedin = profileData.linkedin || existingProfile.linkedin || '';

          // Preserve source from existing profile if available
          if (existingProfile.source && existingProfile.source !== 'unknown') {
            profileData.source = existingProfile.source;
          }
        }
      } catch (e) {
        console.warn('Could not fetch imported profile:', e);
      }
    }

    // FOURTH: Fallback to connection data if still unknown
    if (!profileData.name || profileData.name === 'Unknown') {
      if (connectionData.applicantName && connectionData.applicantName !== 'Unknown Name') {
        profileData.name = connectionData.applicantName;
        console.log('✅ Got name from connection data:', profileData.name);
      }
    }
    if (!profileData.email && connectionData.applicantEmail) {
      profileData.email = connectionData.applicantEmail;
    }

    console.log('📦 Final profile data to archive:', profileData);

    // 2.5. Get Interview History (stored separately from connection data)
    let interviewHistory = null;
    try {
      const interviewsRef = ref(database, `companies/${employerUid}/interviews/${applicantId}`);
      const interviewsSnap = await get(interviewsRef);
      if (interviewsSnap.exists()) {
        interviewHistory = interviewsSnap.val();
        console.log('📅 Found interviews to archive:', Object.keys(interviewHistory).length);
      }
    } catch (e) {
      console.warn('⚠️ Could not fetch interviews for archiving:', e);
    }

    // 3. Construct History Entry
    const timestamp = Date.now();
    const historyId = `${jobId}_${timestamp}`;

    // Determine stage info - handle all-applicants and undefined cases
    let stageId = connectionData.stageId || 'stage-1';
    let substageId = connectionData.substageId || 'all-applicants-1';
    let stageName = 'Immediate Rejection'; // Default for all-applicants

    // If they were in a specific stage, note that
    if (connectionData.stageId && !connectionData.substageId?.startsWith('all-applicants')) {
      stageName = `Rejected from ${connectionData.stageId}`;
    }

    const historyEntry = {
      jobId: jobId || 'unknown_job',
      jobTitle: jobAppliedFor || 'Unknown Job',
      stageId: stageId,
      substageId: substageId,
      stageName: stageName, // Human-readable stage name
      archivedAt: timestamp,
      messages: connectionData.messages || null,
      notes: connectionData.notes || null,
      scores: connectionData.scores || null,
      assessments: connectionData.assessments || null,
      applicantAnalysis: connectionData.applicantAnalysis || null,
      // Preserve the complete job-specific score ledger when the live
      // connection is archived. A scalar fit_score cannot explain which
      // evidence changed the score or distinguish cumulative from instrument fit.
      candidateEvaluation: connectionData.candidateEvaluation || null,
      interviews: interviewHistory,
      isHired: connectionData.isHired || false
    };

    // 4. Update Profile with History
    const targetProfilePath = `imported_profiles/${employerUid}/${applicantId}`;
    const updates = {};

    // Sanitize history entry to remove any undefined values (Firebase throws on undefined)
    const sanitizedHistoryEntry = sanitizeForFirebase(historyEntry);

    // Add history entry
    updates[`${targetProfilePath}/history/${historyId}`] = sanitizedHistoryEntry;

    // Ensure base profile info exists (merge)
    updates[`${targetProfilePath}/name`] = profileData.name;
    updates[`${targetProfilePath}/email`] = profileData.email || '';

    // ALWAYS save CV if we have it
    if (profileData.cv && (profileData.cv.Link || Object.keys(profileData.cv).length > 0)) {
      updates[`${targetProfilePath}/cv`] = profileData.cv;
      console.log('📦 Saving CV to profile:', profileData.cv);
    }

    if (profileData.phone) updates[`${targetProfilePath}/phone`] = profileData.phone;
    if (profileData.linkedin) updates[`${targetProfilePath}/linkedin`] = profileData.linkedin;
    if (profileData.originalUid) updates[`${targetProfilePath}/originalUid`] = profileData.originalUid;

    updates[`${targetProfilePath}/uploadedBy`] = employerUid; // Required by Firebase rules
    updates[`${targetProfilePath}/lastUpdated`] = timestamp;
    updates[`${targetProfilePath}/isArchived`] = true; // Flag for UI filtering if needed
    updates[`${targetProfilePath}/source`] = profileData.source;
    updates[`${targetProfilePath}/createdAt`] = timestamp; // For sorting in ImportedCVs

    // Remove this job from assigned_jobs array
    try {
      const profileRef = ref(database, targetProfilePath);
      const profileSnap = await get(profileRef);
      if (profileSnap.exists()) {
        const currentProfile = profileSnap.val();
        const currentAssignedJobs = currentProfile.assigned_jobs || [];
        const jobTitleToRemove = jobAppliedFor || 'Unknown Job';
        const updatedAssignedJobs = currentAssignedJobs.filter(j => j !== jobTitleToRemove);
        updates[`${targetProfilePath}/assigned_jobs`] = updatedAssignedJobs;
        console.log(`📦 Removed "${jobTitleToRemove}" from assigned_jobs. Remaining:`, updatedAssignedJobs);
      }
    } catch (e) {
      console.warn('⚠️ Could not update assigned_jobs:', e);
    }

    console.log('📦 Writing to path:', targetProfilePath);

    // Final sanitization pass to catch any remaining undefined values
    const sanitizedUpdates = sanitizeForFirebase(updates);
    console.log('📦 Sanitized Updates:', JSON.stringify(sanitizedUpdates, null, 2));

    await update(ref(database), sanitizedUpdates);
    // Recompute extraction after archive/history merge so insights remain current.
    await triggerTalentPoolExtraction(employerUid, applicantId, 'archive_connection', { priority: 'normal' });
    console.log('✅ Connection archived successfully to:', targetProfilePath);
    return true; // Return success

  } catch (error) {
    console.error('❌❌❌ ARCHIVE FAILED ❌❌❌');
    console.error('❌ Error archiving connection:', error);
    console.error('❌ Error code:', error.code);
    console.error('❌ Error message:', error.message);
    console.error('❌ Connection key was:', connectionKey);

    // Don't throw - allow deletion to proceed, but log very prominently
    // This prevents the UI from getting stuck while still alerting developers
    console.warn('⚠️ Proceeding with deletion despite archive failure - candidate data may be lost');
    return false;
  }
};

/**
 * Remove connection with proper index cleanup (bulk version - no custom list count updates)
 */
export const removeConnectionOptimizedBulk = async (connectionKey) => {
  try {
    // 0. Archive connection data first
    console.log('🗑️ removeConnectionOptimizedBulk: Starting for', connectionKey);
    const archiveResult = await archiveConnection(connectionKey);
    console.log('📦 Archive result:', archiveResult ? 'SUCCESS' : 'FAILED');

    // First, get the connection data to know which indexes to clean up
    const connectionRef = ref(database, `connections/${connectionKey}`);
    const connectionSnapshot = await get(connectionRef);

    if (!connectionSnapshot.exists()) {
      throw new Error(`Connection ${connectionKey} not found`);
    }

    const connectionData = connectionSnapshot.val();
    const employerUid = connectionData.employerUid || connectionData.employerId;
    const { jobId } = connectionData;

    if (!employerUid) {
      throw new Error(`No employer ID found in connection data for ${connectionKey}`);
    }

    const updates = {};

    // 1. Remove connection
    updates[`connections/${connectionKey}`] = null;

    // 2. Remove from employer index
    if (employerUid && jobId) {
      updates[`companies/${employerUid}/applicant_index/by_job/${jobId}/${connectionKey}`] = null;
    }

    await update(ref(database), updates);
    return { success: true };
  } catch (error) {
    console.error('Error removing connection optimized (bulk):', error);
    throw error;
  }
};

/**
 * Remove connection with proper index cleanup
 */
export const removeConnectionOptimized = async (connectionKey) => {
  try {
    // 0. Archive connection data first
    console.log('🗑️ removeConnectionOptimized: Starting for', connectionKey);
    const archiveResult = await archiveConnection(connectionKey);
    console.log('📦 Archive result:', archiveResult ? 'SUCCESS' : 'FAILED');

    // First, get the connection data to know which indexes to clean up
    const connectionRef = ref(database, `connections/${connectionKey}`);
    const connectionSnapshot = await get(connectionRef);

    if (!connectionSnapshot.exists()) {
      throw new Error(`Connection ${connectionKey} not found`);
    }

    const connectionData = connectionSnapshot.val();
    const employerUid = connectionData.employerUid || connectionData.employerId;
    const { jobId } = connectionData;

    if (!employerUid) {
      throw new Error(`No employer ID found in connection data for ${connectionKey}`);
    }

    console.log('🗑️ removeConnectionOptimized: Processing connection', { connectionKey });

    const updates = {};

    // 1. Remove connection
    updates[`connections/${connectionKey}`] = null;

    // 2. Remove from employer index (with robust fallback scan)
    if (employerUid) {
      if (jobId) {
        updates[`companies/${employerUid}/applicant_index/by_job/${jobId}/${connectionKey}`] = null;
      }
      // Fallback scan: ensure removal even if jobId is missing/mismatched
      try {
        const byJobRef = ref(database, `companies/${employerUid}/applicant_index/by_job`);
        const byJobSnap = await get(byJobRef);
        if (byJobSnap.exists()) {
          const byJob = byJobSnap.val() || {};
          Object.keys(byJob).forEach((jobKey) => {
            if (byJob[jobKey] && byJob[jobKey][connectionKey]) {
              updates[`companies/${employerUid}/applicant_index/by_job/${jobKey}/${connectionKey}`] = null;
            }
          });
        }
      } catch (scanErr) {
        console.warn('⚠️ Employer index fallback scan failed during removal:', scanErr);
      }
    }

    // 5. Update custom list count if applicable
    if (connectionData.isCustomList && employerUid && jobId) {
      console.log('🗑️ Updating custom list count for:', jobId);
      const customListRef = ref(database, `companies/${employerUid}/custom_lists/${jobId}`);
      const customListSnapshot = await get(customListRef);

      if (customListSnapshot.exists()) {
        const customListData = customListSnapshot.val();
        const currentCount = customListData.applicantCount || 0;
        const newCount = Math.max(0, currentCount - 1);

        updates[`companies/${employerUid}/custom_lists/${jobId}/applicantCount`] = newCount;
        updates[`companies/${employerUid}/custom_lists/${jobId}/lastUpdated`] = Date.now();
        console.log('🗑️ Custom list count: ', currentCount, '→', newCount);
      }
    }

    console.log('🗑️ Applying', Object.keys(updates).length, 'database updates...');
    await update(ref(database), updates);
    console.log('✅ Connection removal completed successfully');
    return { success: true };
  } catch (error) {
    console.error('Error removing connection optimized:', error);
    throw error;
  }
};

// ============================================================================
// CV UPLOAD OPERATIONS  
// ============================================================================

/**
 * Create imported profile for uploaded CV
 */
export const createImportedProfile = async (employerUID, cvData, uploadBatch = null) => {
  try {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const timestamp = Date.now();

    const profileData = {
      email: cvData.email,
      name: cvData.name,
      phone: cvData.phone || '',
      cv: {
        Link: cvData.cvLink,
        fileName: cvData.fileName,
        uploadedAt: timestamp
      },
      parsedData: cvData.parsedData || {},
      uploadedBy: employerUID,
      uploadSource: 'bulk_upload',
      uploadBatch: uploadBatch || `batch_${timestamp}`,
      createdAt: timestamp
    };

    // NEW STRUCTURE: Indexed by employerUID
    await set(ref(database, `imported_profiles/${employerUID}/${tempId}`), profileData);
    // Queue high-priority AI parsing as soon as a standalone CV profile exists.
    await triggerTalentPoolExtraction(employerUID, tempId, 'import_create', { priority: 'high' });
    return tempId;
  } catch (error) {
    console.error('Error creating imported profile:', error);
    throw error;
  }
};

/**
 * Create a custom list for CV uploads
 */
export const createCustomList = async (employerUID, listData) => {
  try {
    const listId = `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const timestamp = Date.now();
    const targetHireDate = normalizeDateInputValue(listData?.hiringPlan?.targetHireDate || listData?.targetHireDate || '');

    const customListData = {
      id: listId,
      name: listData.title,  // Firebase rules expect 'name' field
      title: listData.title, // Keep both for compatibility
      description: listData.description || '',
      jobDescription: listData.jobDescription || '',
      targetHireDate,
      hiringPlan: {
        ...(listData.hiringPlan || {}),
        targetHireDate
      },
      createdAt: timestamp,
      lastUpdated: timestamp,
      applicantCount: 0,
      type: 'custom_list',
      isActive: true,
      createdBy: employerUID
    };

    await update(ref(database), {
      [`companies/${employerUID}/custom_lists/${listId}`]: customListData,
      [`companies/${employerUID}/analytics_v2/job_registry/${listId}`]: buildCustomListJobRegistryRecord(listId, customListData, timestamp)
    });
    invalidateAnalyticsSessionCache(employerUID);
    return { listId, ...customListData };
  } catch (error) {
    console.error('Error creating custom list:', error);
    throw error;
  }
};

/**
 * Load all custom lists for an employer
 */
export const loadEmployerCustomLists = async (employerUID) => {
  try {
    const customListsRef = ref(database, `companies/${employerUID}/custom_lists`);
    const snapshot = await get(customListsRef);

    if (!snapshot.exists()) {
      return [];
    }

    const listsData = snapshot.val();
    return Object.values(listsData)
      .filter(list => list.isActive !== false)
      .sort((a, b) => b.createdAt - a.createdAt);
  } catch (error) {
    console.error('Error loading custom lists:', error);
    return [];
  }
};

/**
 * Load all job options (posted jobs + custom lists) for dropdown
 */
export const loadAllJobOptions = async (employerUID, options = {}) => {
  if (!employerUID) {
    return [];
  }

  const { forceRefresh = false } = options;
  const cachedEntry = jobOptionsCache.get(employerUID);
  const now = Date.now();

  if (!forceRefresh && cachedEntry?.data && (now - cachedEntry.updatedAt) < JOB_OPTIONS_CACHE_TTL_MS) {
    return cachedEntry.data.map(cloneJobOption);
  }

  if (!forceRefresh && cachedEntry?.promise) {
    const inFlightResult = await cachedEntry.promise;
    return inFlightResult.map(cloneJobOption);
  }

  const requestPromise = (async () => {
    const jobsRef = ref(database, `companies/${employerUID}/posts`);
    const customListsRef = ref(database, `companies/${employerUID}/custom_lists`);
    const [jobsSnapshot, listsSnapshot] = await Promise.all([
      get(jobsRef),
      get(customListsRef)
    ]);

    const loadedOptions = [];

    if (jobsSnapshot.exists()) {
      const jobsData = jobsSnapshot.val();
      Object.entries(jobsData).forEach(([jobId, jobData]) => {
        if (jobData.jobOpening) {
          loadedOptions.push({
            id: jobId,
            title: jobData.jobOpening,
            type: 'posted_job',
            subtitle: 'Posted Job',
            applicantCount: jobData.applicantCount || 0,
            description: jobData.jobDescription || '',
            data: jobData
          });
        }
      });
    }

    if (listsSnapshot.exists()) {
      const listsData = listsSnapshot.val();
      Object.values(listsData).forEach(listData => {
        if (listData.isActive !== false) {
          loadedOptions.push({
            id: listData.id,
            title: listData.title,
            type: 'custom_list',
            subtitle: `Custom List (${listData.applicantCount || 0} CVs)`,
            applicantCount: listData.applicantCount || 0,
            description: listData.description || '',
            jobDescription: listData.jobDescription || '',
            data: listData
          });
        }
      });
    }

    return loadedOptions.sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'posted_job' ? -1 : 1;
      }
      return a.title.localeCompare(b.title);
    });
  })();

  jobOptionsCache.set(employerUID, {
    data: cachedEntry?.data || null,
    updatedAt: cachedEntry?.updatedAt || 0,
    promise: requestPromise
  });

  try {
    const loadedOptions = await requestPromise;
    jobOptionsCache.set(employerUID, {
      data: loadedOptions,
      updatedAt: Date.now(),
      promise: null
    });
    return loadedOptions.map(cloneJobOption);
  } catch (error) {
    jobOptionsCache.delete(employerUID);
    console.error('Error loading job options:', error);
    return [];
  }
};

/**
 * Bulk upload CVs with support for both posted jobs and custom lists
 */
export const bulkUploadCVsOptimized = async (employerUID, cvMappings, targetJob) => {
  try {
    const timestamp = Date.now();
    const batchId = `batch_${timestamp}`;
    const updates = {};
    const results = [];

    // Determine if we're uploading to a custom list or posted job
    const isCustomList = targetJob.type === 'custom_list';
    const jobId = targetJob.id;

    for (const cvData of cvMappings) {
      const tempId = `temp_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
      const connectionKey = `${employerUID}_imported_${tempId}`;
      const evaluationStatus = cvData.evaluationStatus
        || cvData.initialEvaluation?.status
        || (normalizeFitScoreValue(cvData.fit_score) !== null ? 'scored' : 'not_configured');

      // Determine substage based on filter result
      let substageId = 'all-applicants-1';
      if (cvData.passedFilter === true) {
        substageId = 'filter-applicants';
      }

      console.log(`📍 ${cvData.name} → ${substageId} (filtered: ${cvData.passedFilter}, customList: ${isCustomList})`);

      // 1. Create imported profile (NEW STRUCTURE)
      updates[`imported_profiles/${employerUID}/${tempId}`] = {
        email: cvData.email,
        name: cvData.name,
        cv: { Link: cvData.cvLink, fileName: cvData.fileName },
        uploadedBy: employerUID,
        uploadBatch: batchId,
        createdAt: timestamp,
        passedFilter: cvData.passedFilter || false,
        evaluationStatus,
        assigned_jobs: [targetJob.title || targetJob.jobOpening || 'Unknown Job']
      };

      // 2. Create connection
      const stageChangeIso = new Date(timestamp).toISOString();
      const connectionPayload = {
        employerUid: employerUID,
        tempProfileId: tempId,
        applicantType: 'imported',
        jobId: jobId,
        roleId: jobId,
        candidateId: tempId,
        jobAppliedFor: targetJob.title,
        department: targetJob.department || '',
        location: targetJob.companyLocation || targetJob.location || '',
        ownerRecruiterId: targetJob.ownerRecruiterId || '',
        stageId: 'stage-1',
        substageId: substageId,
        canMessage: false,
        source: 'bulk_upload',
        primarySource: 'JOBSEEQR',
        sourceCategory: 'import',
        sourceTags: [],
        isCustomList: isCustomList,
        passedFilter: cvData.passedFilter || false,
        evaluationStatus,
        createdAt: timestamp,
        lastStageChange: stageChangeIso
      };
      const fitScore = normalizeFitScoreValue(cvData.fit_score);
      const candidateEvaluation = buildInitialCandidateEvaluation(
        cvData.initialEvaluation,
        fitScore,
        cvData.passedFilter,
        timestamp
      );
      if (candidateEvaluation) {
        const baselineScore = candidateEvaluation.current.fitScore;
        connectionPayload.candidateEvaluation = candidateEvaluation;
        // Compatibility mirrors remain while existing list/card reads migrate
        // to candidateEvaluation.current.fitScore.
        connectionPayload.fit_score = baselineScore;
        connectionPayload.fitScoreUpdatedAt = candidateEvaluation.current.evaluatedAt;
      }
      updates[`connections/${connectionKey}`] = connectionPayload;

      // 3. Update employer index (works for both job posts and custom lists)
      const indexPayload = {
        applicantId: tempId,
        candidateId: tempId,
        applicantType: 'imported',
        applicantName: cvData.name,
        applicantEmail: cvData.email,
        jobTitle: targetJob.title,
        roleId: jobId,
        department: targetJob.department || '',
        location: targetJob.companyLocation || targetJob.location || '',
        ownerRecruiterId: targetJob.ownerRecruiterId || '',
        isCustomList: isCustomList,
        stageId: 'stage-1',
        substageId: substageId,
        lastStageChange: stageChangeIso,
        passedFilter: cvData.passedFilter || false,
        evaluationStatus,
        lastUpdated: timestamp,
        createdAt: timestamp, // Add createdAt for date filtering
        source: 'bulk_upload', // Add source for analytics
        primarySource: 'JOBSEEQR',
        sourceCategory: 'import',
        sourceTags: []
      };
      if (candidateEvaluation) {
        indexPayload.fit_score = candidateEvaluation.current.fitScore;
        indexPayload.fitScoreUpdatedAt = candidateEvaluation.current.evaluatedAt;
      }
      updates[`companies/${employerUID}/applicant_index/by_job/${jobId}/${connectionKey}`] = indexPayload;

      results.push({
        tempId,
        connectionKey,
        name: cvData.name,
        email: cvData.email,
        passedFilter: cvData.passedFilter || false,
        evaluationStatus,
        substageId: substageId
      });
    }

    // 4. Update custom list applicant count if applicable
    if (isCustomList) {
      const currentCountRef = ref(database, `companies/${employerUID}/custom_lists/${jobId}/applicantCount`);
      const currentCountSnapshot = await get(currentCountRef);
      const currentCount = currentCountSnapshot.val() || 0;

      updates[`companies/${employerUID}/custom_lists/${jobId}/applicantCount`] = currentCount + cvMappings.length;
      updates[`companies/${employerUID}/custom_lists/${jobId}/lastUpdated`] = timestamp;
    }

    await update(ref(database), updates);
    // Queue batch parsing in one request so very large imports remain tractable.
    await triggerTalentPoolExtractionBulk(
      employerUID,
      results.map((item) => item.tempId),
      'bulk_upload',
      { priority: results.length >= 250 ? 'low' : 'normal' }
    );
    return { success: true, batchId, results, isCustomList };
  } catch (error) {
    console.error('Error bulk uploading CVs optimized:', error);
    throw error;
  }
};

/**
 * Update custom list details
 */
export const updateCustomList = async (employerUID, listId, updates) => {
  try {
    // First get the current list data to check if title is changing
    const listRef = ref(database, `companies/${employerUID}/custom_lists/${listId}`);
    const currentSnapshot = await get(listRef);

    if (!currentSnapshot.exists()) {
      throw new Error('Custom list not found');
    }

    const currentData = currentSnapshot.val();
    const oldTitle = currentData.title;
    const newTitle = updates.title;

    const updateData = {
      ...updates,
      lastUpdated: Date.now()
    };

    // Update the custom list data
    await update(listRef, updateData);
    await update(
      ref(database, `companies/${employerUID}/analytics_v2/job_registry/${listId}`),
      buildCustomListJobRegistryRecord(listId, { ...currentData, ...updateData }, updateData.lastUpdated)
    );
    invalidateAnalyticsSessionCache(employerUID);

    // If title changed, update all applicant index entries that reference this list
    if (oldTitle !== newTitle && oldTitle && newTitle) {
      console.log(`Updating references from "${oldTitle}" to "${newTitle}"`);

      // Get all applicants for this custom list
      const indexRef = ref(database, `companies/${employerUID}/applicant_index/by_job/${listId}`);
      const indexSnapshot = await get(indexRef);

      if (indexSnapshot.exists()) {
        const applicants = indexSnapshot.val();
        const batchUpdates = {};

        // Update job title in each applicant's index entry
        Object.entries(applicants).forEach(([connectionKey, applicantData]) => {
          batchUpdates[`companies/${employerUID}/applicant_index/by_job/${listId}/${connectionKey}/jobTitle`] = newTitle;
        });

        // Apply all updates atomically
        if (Object.keys(batchUpdates).length > 0) {
          await update(ref(database), batchUpdates);
          console.log(`Updated ${Object.keys(batchUpdates).length} applicant references`);
        }
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating custom list:', error);
    throw error;
  }
};

/**
 * Delete custom list and all associated applicants
 */
export const deleteCustomList = async (employerUID, listId) => {
  try {
    // First, get all connections for this custom list
    const indexRef = ref(database, `companies/${employerUID}/applicant_index/by_job/${listId}`);
    const listRef = ref(database, `companies/${employerUID}/custom_lists/${listId}`);
    const [indexSnapshot, listSnapshot] = await Promise.all([
      get(indexRef),
      get(listRef)
    ]);

    const updates = {};
    const deletedAt = Date.now();
    const deletedListData = {
      ...(listSnapshot.exists() ? listSnapshot.val() : {}),
      isActive: false,
      deleted: true,
      deletedAt
    };

    // Delete the custom list
    updates[`companies/${employerUID}/custom_lists/${listId}`] = null;
    updates[`companies/${employerUID}/analytics_v2/job_registry/${listId}`] = {
      ...buildCustomListJobRegistryRecord(listId, deletedListData, deletedAt),
      lifecycleStatus: 'deleted',
      deleted: true,
      deletedAt: new Date(deletedAt).toISOString()
    };

    // Delete applicant index entries
    updates[`companies/${employerUID}/applicant_index/by_job/${listId}`] = null;

    // Delete connections and imported profiles if they exist
    if (indexSnapshot.exists()) {
      const applicants = indexSnapshot.val();

      for (const [connectionKey, applicantData] of Object.entries(applicants)) {
        // Delete connection
        updates[`connections/${connectionKey}`] = null;

        // Delete imported profile if it's an imported applicant
        if (applicantData.applicantType === 'imported') {
          // OLD PATH
          updates[`imported_profiles/${applicantData.applicantId}`] = null;
          // NEW PATH
          updates[`imported_profiles/${employerUID}/${applicantData.applicantId}`] = null;
        }
      }
    }

    await update(ref(database), updates);
    const deletedAtIso = new Date(deletedAt).toISOString();
    await Promise.allSettled([
      emitAtsAnalyticsEvent({
        company_uid: employerUID,
        event_type: 'job_deleted',
        occurred_at: deletedAtIso,
        dedupe_key: `job_deleted:${employerUID}:${listId}:${deletedAtIso}`,
        job: {
          jobId: listId,
          roleId: listId,
          jobTitle: deletedListData.title || deletedListData.name || listId,
          department: deletedListData.department || '',
          location: deletedListData.location || '',
          lifecycleStatus: 'deleted',
          deleted: true
        },
        context: {
          originAction: 'custom_list_delete',
          isCustomList: true,
          deletedAt: deletedAtIso
        }
      }),
      ...Object.entries(indexSnapshot.exists() ? indexSnapshot.val() : {}).map(([connectionKey, applicantData = {}]) =>
        emitAtsAnalyticsEvent({
          company_uid: employerUID,
          event_type: 'candidate_removed_from_job',
          occurred_at: deletedAtIso,
          dedupe_key: `candidate_removed_from_job:${employerUID}:${listId}:${connectionKey}:${deletedAtIso}`,
          subject: {
            connectionKey,
            applicantId: applicantData.applicantId || applicantData.candidateId || '',
            candidateId: applicantData.candidateId || applicantData.applicantId || '',
            applicantType: applicantData.applicantType || 'unknown',
            outcomeState: 'removed'
          },
          job: {
            jobId: listId,
            roleId: listId,
            jobTitle: deletedListData.title || deletedListData.name || applicantData.jobTitle || listId,
            department: deletedListData.department || applicantData.department || '',
            location: deletedListData.location || applicantData.location || '',
            lifecycleStatus: 'deleted'
          },
          context: {
            originAction: 'custom_list_delete',
            isCustomList: true,
            stageId: applicantData.stageId || '',
            substageId: applicantData.substageId || ''
          }
        })
      )
    ]);
    invalidateAnalyticsSessionCache(employerUID);
    return { success: true };
  } catch (error) {
    console.error('Error deleting custom list:', error);
    throw error;
  }
};

/**
 * Move applicants from custom list to posted job
 */
export const moveApplicantsToJob = async (employerUID, fromListId, toJobId, applicantConnectionKeys) => {
  try {
    // Get target job data
    const jobRef = ref(database, `companies/${employerUID}/posts/${toJobId}`);
    const jobSnapshot = await get(jobRef);

    if (!jobSnapshot.exists()) {
      throw new Error('Target job not found');
    }

    const jobData = jobSnapshot.val();
    const updates = {};
    const timestamp = Date.now();

    for (const connectionKey of applicantConnectionKeys) {
      // Update connection
      updates[`connections/${connectionKey}/jobId`] = toJobId;
      updates[`connections/${connectionKey}/jobAppliedFor`] = jobData.jobOpening;
      updates[`connections/${connectionKey}/isCustomList`] = false;
      updates[`connections/${connectionKey}/lastStageChange`] = new Date().toISOString();

      // Get current applicant data
      const currentIndexRef = ref(database, `companies/${employerUID}/applicant_index/by_job/${fromListId}/${connectionKey}`);
      const currentIndexSnapshot = await get(currentIndexRef);

      if (currentIndexSnapshot.exists()) {
        const applicantData = currentIndexSnapshot.val();

        // Move to new job index
        updates[`companies/${employerUID}/applicant_index/by_job/${toJobId}/${connectionKey}`] = {
          ...applicantData,
          jobTitle: jobData.jobOpening,
          isCustomList: false,
          lastUpdated: timestamp
        };

        // Remove from old index
        updates[`companies/${employerUID}/applicant_index/by_job/${fromListId}/${connectionKey}`] = null;
      }
    }

    // Update custom list applicant count
    const currentCountRef = ref(database, `companies/${employerUID}/custom_lists/${fromListId}/applicantCount`);
    const currentCountSnapshot = await get(currentCountRef);
    const currentCount = currentCountSnapshot.val() || 0;

    updates[`companies/${employerUID}/custom_lists/${fromListId}/applicantCount`] = Math.max(0, currentCount - applicantConnectionKeys.length);
    updates[`companies/${employerUID}/custom_lists/${fromListId}/lastUpdated`] = timestamp;

    await update(ref(database), updates);
    return { success: true };
  } catch (error) {
    console.error('Error moving applicants to job:', error);
    throw error;
  }
};

// ============================================================================
// NOTIFICATION OPERATIONS
// ============================================================================

// Calculate which checklist day corresponds to today's date
const calculateChecklistDayOptimized = (startDate, today) => {
  // Only show notifications on business days (Monday-Friday)
  const todayDayOfWeek = today.getDay();
  if (todayDayOfWeek === 0 || todayDayOfWeek === 6) {
    return null; // No notifications on weekends
  }

  // Normalize dates to avoid time zone issues
  const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const normalizedStartDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());

  // If today is before start date, no notification
  if (normalizedToday < normalizedStartDate) {
    return null;
  }

  // Calculate business days elapsed from start date to today
  let currentDate = new Date(normalizedStartDate);
  let businessDaysElapsed = 0;

  while (currentDate <= normalizedToday) {
    // If this is a business day, increment counter
    if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
      // Check if we've reached today
      if (currentDate.toDateString() === normalizedToday.toDateString()) {
        // Today is a business day - check if it's within the 5-day period
        if (businessDaysElapsed < 5) {
          return `day${businessDaysElapsed + 1}`;
        } else {
          return null; // Beyond the 5-day checklist period
        }
      }
      businessDaysElapsed++;
    }

    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return null;
};

// Calculate the actual date for a given checklist day
const calculateActualDateOptimized = (startDate, dayKey) => {
  const dayIndex = parseInt(dayKey.replace('day', '')) - 1;
  let currentDate = new Date(startDate);
  let businessDaysAdded = 0;

  while (businessDaysAdded < dayIndex) {
    currentDate.setDate(currentDate.getDate() + 1);
    // Skip weekends
    if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
      businessDaysAdded++;
    }
  }

  return currentDate;
};

/**
 * Get notifications using optimized queries
 */
export const getNotificationsOptimized = async (employerUID) => {
  try {
    // Get connections data to check for messages and assessments
    const connectionsRef = ref(database, 'connections');
    const connectionsSnapshot = await get(connectionsRef);

    if (!connectionsSnapshot.exists()) {
      return [];
    }

    const connections = connectionsSnapshot.val();
    const notifications = [];
    const now = Date.now();

    // Process each connection for notifications
    for (const [connectionId, data] of Object.entries(connections)) {
      if (!connectionId.startsWith(`${employerUID}_`)) continue;

      // CRITICAL FIX: Properly extract applicantId for both imported and authenticated candidates
      let applicantId, applicantType;
      if (connectionId.includes('_imported_')) {
        // Format: {employerUID}_imported_{applicantId}
        applicantId = connectionId.split('_imported_')[1];
        applicantType = 'imported';
      } else {
        // Format: {employerUID}_{applicantId}
        applicantId = connectionId.split('_')[1];
        applicantType = 'authenticated';
      }

      // Get person data for candidate name
      let candidateName = 'Unknown Candidate';
      try {
        if (applicantType === 'imported') {
          // Get name from imported_profiles node (try new structure then old)
          let profileRef = ref(database, `imported_profiles/${employerUID}/${applicantId}`);
          let profileSnapshot = await get(profileRef);
          if (!profileSnapshot.exists()) {
            profileRef = ref(database, `imported_profiles/${applicantId}`);
            profileSnapshot = await get(profileRef);
          }

          const profileData = profileSnapshot.val();
          candidateName = profileData?.name || profileData?.username || 'Unknown Candidate';
        } else {
          // Get name from people node (authenticated users)
          const personRef = ref(database, `people/${applicantId}`);
          const personSnapshot = await get(personRef);
          const personData = personSnapshot.val();
          candidateName = personData?.username || 'Unknown Candidate';
        }
      } catch (error) {
        console.error('Error fetching person data:', error);
      }

      // 1. Check for unread chat messages
      if (data.newMsgEmpl === "true") {
        notifications.push({
          id: `chat_${connectionId}`,
          type: 'unread_chat',
          title: 'New Message',
          description: `You have a new message from ${candidateName}. Make sure to respond promptly.`,
          candidateName: candidateName,
          jobTitle: data.jobAppliedFor || 'Unknown Position',
          jobId: data.jobId || '',
          applicantId: applicantId,
          timestamp: data.latestMessage || now,
          unread: true
        });
      }

      // 2. Check for completed assessments
      if (data.assessments) {
        for (const [assessmentId, assessmentData] of Object.entries(data.assessments)) {
          // Check for assessments that are completed OR evaluated but haven't sent notification yet
          const isCompleted = assessmentData.response?.status === 'completed';
          const isEvaluated = ['evaluated', 'manual_review'].includes(assessmentData.response?.status);
          const notificationNotSent = !assessmentData.notificationSent;

          if ((isCompleted || isEvaluated) && notificationNotSent) {
            // CRITICAL FIX: Fetch assessment title from companies node, not connections!
            let assessmentTitle = 'an assessment';
            try {
              const assessmentRef = ref(database, `companies/${employerUID}/assessments/${assessmentId}`);
              const assessmentSnapshot = await get(assessmentRef);
              if (assessmentSnapshot.exists()) {
                const assessmentDetails = assessmentSnapshot.val();
                assessmentTitle = assessmentDetails.title || assessmentDetails.name || 'an assessment';
              }
            } catch (error) {
              console.warn('Could not fetch assessment title for notification:', error);
            }

            notifications.push({
              id: `assessment_${connectionId}_${assessmentId}`,
              type: 'assessment_completed',
              title: 'Assessment Completed',
              description: `${candidateName || 'A candidate'} has completed ${assessmentTitle}. Click to review their responses.`,
              assessmentTitle,
              candidateName: candidateName || 'Unknown Candidate',
              jobTitle: data.jobAppliedFor || 'Unknown Position',
              jobId: data.jobId || '',
              applicantId: applicantId,
              timestamp: assessmentData.response.submittedAt || now,
              unread: true
            });
          }
        }
      }

      // 3. Check for unmoved candidates (7+ days in same stage)
      const lastStageChange = data.lastStageChange;
      if (lastStageChange) {
        const moveTime = new Date(lastStageChange).getTime();
        const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;
        const ONE_WEEK_MS_DISMISS = 7 * 24 * 60 * 60 * 1000; // Don't show again for 7 days after dismissal

        if ((now - moveTime) > TWO_WEEKS_MS) {
          // Check if notification was recently dismissed
          const dismissedTime = data.unmovedNotificationDismissed;
          let shouldShow = true;

          if (dismissedTime) {
            const dismissedTimestamp = new Date(dismissedTime).getTime();
            const timeSinceDismissed = now - dismissedTimestamp;

            // Don't show notification if it was dismissed within the last week
            if (timeSinceDismissed < ONE_WEEK_MS_DISMISS) {
              shouldShow = false;
            }
          }

          if (shouldShow) {
            const timeSinceMove = Math.floor((now - moveTime) / (1000 * 60 * 60 * 24));
            let timeText;

            if (timeSinceMove >= 30) {
              const months = Math.floor(timeSinceMove / 30);
              timeText = months === 1 ? 'one month' : `${months} months`;
            } else if (timeSinceMove >= 14) {
              const weeks = Math.floor(timeSinceMove / 7);
              timeText = weeks === 1 ? 'one week' : `${weeks} weeks`;
            } else {
              timeText = 'over a week';
            }

            notifications.push({
              id: `unmoved_${connectionId}`,
              type: 'unmoved_candidate',
              title: 'Candidate Needs Attention',
              description: `${candidateName} has been in the same stage for ${timeText}. Please review their application and make a decision on whether to move them to the next stage.`,
              durationDays: timeSinceMove,
              candidateName: candidateName,
              jobTitle: data.jobAppliedFor || 'Unknown Position',
              jobId: data.jobId || '',
              applicantId: applicantId,
              timestamp: now,
              unread: true
            });
          }
        }
      }

      // 4. Check for First Week Checklist notifications
      if (data.firstWeekChecklist && data.checklistProgress) {
        const checklistData = data.firstWeekChecklist;
        const progressData = data.checklistProgress;

        // Only show notifications if there's a start date
        if (!checklistData.startDate) continue;

        // Calculate which checklist day corresponds to today's actual date
        const today = new Date();
        const startDate = new Date(checklistData.startDate);
        const todayDayKey = calculateChecklistDayOptimized(startDate, today);

        if (todayDayKey && checklistData.checklist && checklistData.checklist[todayDayKey]) {
          const todayTasks = checklistData.checklist[todayDayKey];
          const todayProgress = progressData[todayDayKey] || {};

          const completedTasks = Object.values(todayProgress).filter(Boolean).length;
          const totalTasks = todayTasks.length;
          const remainingTasks = totalTasks - completedTasks;

          if (remainingTasks > 0) {
            // Calculate the actual date for this checklist day
            const checklistDate = calculateActualDateOptimized(startDate, todayDayKey);
            const dayName = checklistDate.toLocaleDateString(getUiLocale(), { weekday: 'long' });
            const dateStr = checklistDate.toLocaleDateString(getUiLocale(), { month: 'short', day: 'numeric' });
            const dayIndex = parseInt(todayDayKey.replace('day', ''));
            const dayDisplay = `Day ${dayIndex} (${dayName}, ${dateStr})`;

            notifications.push({
              id: `checklist_${connectionId}_${todayDayKey}`,
              type: 'checklist_reminder',
              title: 'First Week Tasks Pending',
              description: `${candidateName} has ${remainingTasks} task${remainingTasks > 1 ? 's' : ''} remaining for ${dayDisplay}. Click to view their checklist progress.`,
              candidateName: candidateName,
              jobTitle: data.jobAppliedFor || 'Unknown Position',
              jobId: data.jobId || '',
              applicantId: applicantId,
              timestamp: now,
              unread: true,
              remainingTasks: remainingTasks,
              totalTasks: totalTasks,
              day: dayDisplay,
              dayDate: checklistDate.toISOString(),
              dayIndex
            });
          }
        }
      }
    }

    return notifications;
  } catch (error) {
    console.error('Error getting notifications optimized:', error);
    return [];
  }
};

// ============================================================================
// MIGRATION UTILITIES
// ============================================================================

/**
 * Calculates the Levenshtein distance between two strings, which is a measure of their similarity.
 */
const getLevenshteinDistance = (a, b) => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = Array(a.length + 1).fill(null).map(() => Array(b.length + 1).fill(null));

  for (let i = 0; i <= a.length; i++) {
    matrix[i][0] = i;
  }
  for (let j = 0; j <= b.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,      // Deletion
        matrix[i][j - 1] + 1,      // Insertion
        matrix[i - 1][j - 1] + cost // Substitution
      );
    }
  }

  return matrix[a.length][b.length];
};

/**
 * Migrate existing connections to new indexed structure
 * This should be run once to migrate existing data
 */
export const migrateToOptimizedStructure = async (employerUID) => {
  try {
    console.log(`Starting migration for employer: ${employerUID}`);

    // Get all existing connections for this employer
    const connectionsRef = ref(database, 'connections');
    const connectionsSnapshot = await get(connectionsRef);

    if (!connectionsSnapshot.exists()) {
      console.log('No connections to migrate');
      return { success: true, migrated: 0 };
    }

    // Get all job posts to match applicants to jobs
    const jobsRef = ref(database, `companies/${employerUID}/posts`);
    const jobsSnapshot = await get(jobsRef);
    const jobsData = jobsSnapshot.val() || {};
    const jobTitleToIdMap = {};
    Object.entries(jobsData).forEach(([jobId, jobData]) => {
      if (jobData.jobOpening) {
        // Trim and convert to lowercase for case-insensitive matching
        jobTitleToIdMap[jobData.jobOpening.trim().toLowerCase()] = jobId;
      }
    });
    console.log('Migration Job Map:', jobTitleToIdMap);

    const allConnections = connectionsSnapshot.val();
    const employerConnections = Object.entries(allConnections)
      .filter(([key]) => key.startsWith(`${employerUID}_`));

    if (employerConnections.length === 0) {
      console.log('No connections found for this employer');
      return { success: true, migrated: 0 };
    }

    const updates = {};
    let migrated = 0;

    // Process each connection
    for (const [connectionKey, connectionData] of employerConnections) {
      const applicantId = connectionData.jobSeekerUid || connectionData.tempProfileId;
      const applicantType = connectionData.applicantType || 'authenticated';

      // Smartly determine the jobId using fuzzy string matching
      const jobTitle = connectionData.jobAppliedFor;
      let bestMatch = { jobId: 'legacy', score: 0 };

      if (jobTitle) {
        const cleanedConnectionTitle = jobTitle.trim().toLowerCase();
        for (const [postTitle, postId] of Object.entries(jobTitleToIdMap)) {
          const distance = getLevenshteinDistance(cleanedConnectionTitle, postTitle);
          const similarity = 1 - distance / Math.max(cleanedConnectionTitle.length, postTitle.length);

          if (similarity > bestMatch.score) {
            bestMatch = { jobId: postId, score: similarity };
          }
        }
      }

      const SIMILARITY_THRESHOLD = 0.8; // 80% similarity required for a match
      const jobId = bestMatch.score >= SIMILARITY_THRESHOLD ? bestMatch.jobId : 'legacy';

      console.log(`Migrating applicant ${applicantId} for job: "${jobTitle}". Best match score: ${bestMatch.score.toFixed(2)}. Final Job ID: ${jobId}`);

      // Get applicant name
      let applicantName = 'Unknown Name';
      let applicantEmail = '';

      if (applicantType === 'authenticated' && connectionData.jobSeekerUid) {
        try {
          const personRef = ref(database, `people/${connectionData.jobSeekerUid}/username`);
          const personSnapshot = await get(personRef);
          applicantName = personSnapshot.val() || 'Unknown Name';
        } catch (error) {
          console.warn(`Could not get name for applicant ${connectionData.jobSeekerUid}`);
        }
      }

      const now = new Date();
      const timestamp = now.getTime();

      // Ensure the connection object is fully compliant with the new schema.
      // We will update the connection object in place in the `updates` batch.
      if (!connectionData.jobId && jobId !== 'legacy') {
        updates[`connections/${connectionKey}/jobId`] = jobId;
      }
      if (!connectionData.applicantType) {
        updates[`connections/${connectionKey}/applicantType`] = applicantType;
      }
      if (!connectionData.stageId) {
        updates[`connections/${connectionKey}/stageId`] = 'stage-1';
      }
      if (!connectionData.substageId) {
        updates[`connections/${connectionKey}/substageId`] = 'all-applicants-1';
      }
      if (!connectionData.lastStageChange) {
        updates[`connections/${connectionKey}/lastStageChange`] = now.toISOString();
      }
      if (typeof connectionData.canMessage === 'undefined') {
        updates[`connections/${connectionKey}/canMessage`] = (applicantType === 'authenticated');
      }
      if (typeof connectionData.accepted === 'undefined') {
        updates[`connections/${connectionKey}/accepted`] = true;
      }
      if (!connectionData.createdAt) {
        updates[`connections/${connectionKey}/createdAt`] = timestamp;
      }

      // Create employer index entry
      // We use || here to catch both data that was already present and data we are adding in this batch update.
      updates[`companies/${employerUID}/applicant_index/by_job/${jobId}/${connectionKey}`] = {
        applicantId,
        applicantType,
        applicantName,
        applicantEmail,
        jobTitle: connectionData.jobAppliedFor || 'Unknown Job',
        stageId: connectionData.stageId || 'stage-1',
        substageId: connectionData.substageId || 'all-applicants-1',
        createdAt: connectionData.createdAt || timestamp, // Preserve original createdAt
        lastUpdated: timestamp
      };

      migrated++;
    }

    // Apply all updates in a single batch
    await update(ref(database), updates);

    console.log(`Migration completed. Migrated ${migrated} connections for employer ${employerUID}`);
    return { success: true, migrated };
  } catch (error) {
    console.error('Error during migration:', error);
    throw error;
  }
};

const DatabaseOptimizer = {
  // Core utilities
  generateConnectionKey,
  parseConnectionKey,

  // Employer operations
  loadEmployerApplicantsOptimized,
  loadJobApplicantsOptimized,

  // Write operations
  createConnectionOptimized,
  updateApplicantPositionOptimized,
  removeConnectionOptimized,
  removeConnectionOptimizedBulk,
  archiveConnection, // NEW: Export archive connection

  // CV uploads
  createImportedProfile,
  bulkUploadCVsOptimized,

  // Custom lists
  createCustomList,
  loadEmployerCustomLists,
  loadAllJobOptions,
  updateCustomList,
  deleteCustomList,
  moveApplicantsToJob,

  // Notifications
  getNotificationsOptimized,

  // Migration
  migrateToOptimizedStructure
};

export default DatabaseOptimizer;
